import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { DURATION, EASE } from "./animation-config.js";

gsap.registerPlugin(ScrollTrigger);

const HIDE_MS = 150;
const HEADLINE_X = "-100vw";
const CONTENT_X = "80vw";

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

function getItemCategories(item) {
  return (item.dataset.teamCategories || "").split(/\s+/).filter(Boolean);
}

function itemMatchesFilter(item, slug) {
  return Boolean(slug && getItemCategories(item).includes(slug));
}

function setFilterActive(filters, activeFilter) {
  filters.forEach((filter) => {
    const isActive = filter === activeFilter;

    filter.classList.toggle("is-active", isActive);
    filter.setAttribute("aria-selected", isActive ? "true" : "false");
  });
}

function setItemHidden(item, hidden) {
  item.hidden = hidden;

  if (hidden) {
    item.classList.remove("is-leaving", "is-entering");
  }
}

function applyTeamFilter(section, slug) {
  section.querySelectorAll("[data-team-item]").forEach((item) => {
    setItemHidden(item, !itemMatchesFilter(item, slug));
  });
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function applyTeamFilterAnimated(section, slug) {
  const items = [...section.querySelectorAll("[data-team-item]")];
  const leaving = items.filter((item) => !item.hidden);
  const entering = items.filter(
    (item) => item.hidden && itemMatchesFilter(item, slug),
  );

  leaving.forEach((item) => {
    item.classList.add("is-leaving");
  });

  await wait(HIDE_MS);

  items.forEach((item) => {
    item.classList.remove("is-leaving");

    if (!itemMatchesFilter(item, slug)) {
      setItemHidden(item, true);
    }
  });

  entering.forEach((item) => {
    item.hidden = false;
    item.classList.add("is-entering");
  });

  await new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

  entering.forEach((item) => {
    item.classList.remove("is-entering");
  });
}

function initTeamFilters(section) {
  const filters = [...section.querySelectorAll("[data-team-filter]")];

  if (!filters.length) {
    return;
  }

  let isAnimating = false;

  const defaultFilter = filters.find((filter) =>
    filter.classList.contains("is-active"),
  );

  if (defaultFilter) {
    applyTeamFilter(section, defaultFilter.dataset.teamFilter);
  }

  filters.forEach((filter) => {
    filter.addEventListener("click", async () => {
      const slug = filter.dataset.teamFilter;

      if (!slug || filter.classList.contains("is-active") || isAnimating) {
        return;
      }

      setFilterActive(filters, filter);

      if (reducedMotion) {
        applyTeamFilter(section, slug);
        return;
      }

      isAnimating = true;

      try {
        await applyTeamFilterAnimated(section, slug);
      } finally {
        isAnimating = false;
      }
    });
  });
}

function initTeamReveal(section) {
  const headline = section.querySelector(".team__headline");
  const filters = section.querySelector(".team__filters");
  const grid = section.querySelector(".team__grid");

  if (!headline && !filters && !grid) {
    return;
  }

  if (reducedMotion) {
    if (headline) {
      gsap.set(headline, { x: 0, opacity: 1 });
    }

    if (filters) {
      gsap.set(filters, { x: 0, opacity: 1 });
    }

    if (grid) {
      gsap.set(grid, { x: 0, opacity: 1 });
    }

    gsap.set(section, { "--team-overlay-opacity": 0 });
    section.classList.add("is-in-view");
    return;
  }

  gsap.set(section, { "--team-overlay-opacity": 1 });

  if (headline) {
    gsap.set(headline, { x: HEADLINE_X, opacity: 0 });
  }

  if (filters) {
    gsap.set(filters, { x: CONTENT_X, opacity: 0 });
  }

  if (grid) {
    gsap.set(grid, { x: CONTENT_X, opacity: 0 });
  }

  ScrollTrigger.create({
    trigger: section,
    start: "top 70%",
    once: true,
    onEnter: () => {
      section.classList.add("is-in-view");

      const timeline = gsap.timeline({
        defaults: { duration: DURATION, ease: EASE },
      });

      timeline.to(section, { "--team-overlay-opacity": 0 }, 0);

      if (headline) {
        timeline.to(headline, { x: 0, opacity: 1 }, 0);
      }

      if (filters) {
        timeline.to(filters, { x: 0, opacity: 1 }, 0);
      }

      if (grid) {
        timeline.to(grid, { x: 0, opacity: 1 }, 0);
      }
    },
  });
}

function initTeamSection(section) {
  initTeamReveal(section);
  initTeamFilters(section);
}

document.querySelectorAll("[data-team]").forEach(initTeamSection);

window.addEventListener(
  "load",
  () => {
    ScrollTrigger.refresh();
  },
  { once: true },
);
